import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid2,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  FilterList,
  VisibilityOutlined,
  FileDownloadOutlined,
  PictureAsPdfOutlined,
  BusinessOutlined,
  LocationOnOutlined,
  CategoryOutlined,
  TableChartOutlined,
  BarChartOutlined,
} from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { getFacilitiyV2, getStatePersonalizedV2 } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Recharts Components
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

export default function LgaSectorReport({ props }) {
  const theme = useTheme();
  const reportRef = useRef(null);
  const chartRef = useRef(null);

  // Tab State: 0 = Tabular, 1 = Graphical
  const [activeTab, setActiveTab] = useState(0);

  // Control state variables
  const initState = { stateId: '', stateName: '' };
  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateSelected, setStateSelected] = useState(initState);

  // Data loading state
  const [facilityList, setFacilityList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const pageSize = 10000;

  useEffect(() => {
    fetchStateList();
  }, []);

  const fetchStateList = async () => {
    setStateLoading(true);
    try {
      const res = await getStatePersonalizedV2();
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setStateList(data || []);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({
        type: 'error',
        message: error?.data?.result?.status?.desc || 'Error fetching states.',
      });
    } finally {
      setStateLoading(false);
    }
  };

  const handlePreviewReport = async () => {
    if (!stateSelected?.stateId) {
      openNotification({ type: 'warning', message: 'Please select a state first.' });
      return;
    }

    setLoading(true);
    try {
      const res = await getFacilitiyV2(
        '',
        '',
        '',
        '',
        stateSelected?.stateId || '',
        '',
        '',
        '',
        pageNum,
        pageSize
      );

      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setFacilityList(data || []);
      } else {
        openNotification({
          type: 'error',
          message: status?.desc || 'Failed to fetch facility data',
        });
      }
    } catch (error) {
      const errmsg = error?.data?.result?.data?.status?.desc || 'Error fetching data';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setLoading(false);
    }
  };

  // Group facility data by Sector Name
  const groupBySector = (data) => {
    return data.reduce((acc, curr) => {
      const sector = curr.sectorName?.trim() || 'UNSPECIFIED SECTOR';
      if (!acc[sector]) acc[sector] = [];
      acc[sector].push(curr);
      return acc;
    }, {});
  };

  // Group facility data by LGA Name (for Pie Chart analytics)
  const groupByLga = (data) => {
    return data.reduce((acc, curr) => {
      const lga = curr.lgaName?.trim() || 'UNSPECIFIED LGA';
      if (!acc[lga]) acc[lga] = [];
      acc[lga].push(curr);
      return acc;
    }, {});
  };

  // Prepare Chart Data for Sector Bar Chart
  const getSectorChartData = () => {
    const grouped = groupBySector(facilityList);
    return Object.keys(grouped).map((sectorName) => ({
      sectorName,
      count: grouped[sectorName].length,
    }));
  };

  // Prepare Chart Data for LGA Pie Chart
  const getLgaChartData = () => {
    const lgaCounts = {};
    facilityList.forEach((fac) => {
      const lga = fac.lgaName?.trim() || 'UNSPECIFIED';
      lgaCounts[lga] = (lgaCounts[lga] || 0) + 1;
    });

    return Object.keys(lgaCounts).map((lga) => ({
      name: lga,
      value: lgaCounts[lga],
    }));
  };

  // Export Table Data to Excel
  const exportToExcel = () => {
    if (!facilityList || facilityList.length === 0) return;

    const workbook = XLSX.utils.book_new();
    const sheetData = [
      [`List of Industries in ${stateSelected?.stateName || 'State'} State by Sectors`],
      [], // Blank row
      ['FACILITY NAME', 'SECTOR', 'CATEGORY', 'LOCAL GOVERNMENT', 'ADDRESS'],
    ];

    const grouped = groupBySector(facilityList);
    Object.keys(grouped).forEach((sectorName) => {
      // Add Sector Header Row
      sheetData.push([`--- ${sectorName.toUpperCase()} ---`, '', '', '', '']);
      grouped[sectorName].forEach((fac) => {
        sheetData.push([
          fac.facilityName || '-',
          fac.sectorName || '-',
          fac.facilityCategoryName || '-',
          fac.lgaName || '-',
          fac.address || '-',
        ]);
      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    worksheet['!cols'] = [{ wch: 35 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 40 }];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sectors_Report');
    XLSX.writeFile(
      workbook,
      `List_of_Industries_${stateSelected?.stateName || 'State'}_Sectors.xlsx`
    );
  };

  // Export Active Tab to Multipage PDF
  const exportToPDF = async () => {
    const targetElement = activeTab === 0 ? reportRef.current : chartRef.current;
    if (!targetElement) return;

    try {
      setLoading(true);

      const canvas = await html2canvas(targetElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        onclone: (clonedDoc) => {
          if (activeTab === 0) {
            const clonedReport = clonedDoc.querySelector('#report-pdf-container');
            if (clonedReport) {
              clonedReport.style.padding = '20px';
              const title = clonedReport.querySelector('.report-title');
              if (title) title.style.fontSize = '16px';
              const cardTexts = clonedReport.querySelectorAll('.metric-value');
              cardTexts.forEach((t) => (t.style.fontSize = '16px'));
              const tableCells = clonedReport.querySelectorAll('.MuiTableCell-root');
              tableCells.forEach((cell) => {
                cell.style.fontSize = '10px';
                cell.style.padding = '4px 8px';
                cell.style.lineHeight = '1.2';
              });
            }
          }
        },
      });

      const imgWidth = 210; // A4 Width in mm
      const pageHeight = 297; // A4 Height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');

      // First Page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Subsequent Pages
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileNamePrefix = activeTab === 0 ? 'Sector_Report' : 'Sector_Analytics';
      pdf.save(`${fileNamePrefix}_${stateSelected?.stateName || 'State'}.pdf`);
    } catch (error) {
      console.error('PDF Export Error:', error);
      openNotification({ type: 'error', message: 'Failed to generate PDF.' });
    } finally {
      setLoading(false);
    }
  };

  // Summary Metrics Calculation
  const groupedSectors = groupBySector(facilityList);
  const totalLgas = Object.keys(groupByLga(facilityList)).length;
  const totalSectors = Object.keys(groupedSectors).length;

  const COLORS = ['#1b5e20', '#0288d1', '#ed6c02', '#9c27b0', '#d32f2f', '#2e7d32', '#009688'];

  // Render Graphical Tab View
  const renderChartsView = () => {
    const sectorChartData = getSectorChartData();
    const lgaChartData = getLgaChartData();

    return (
      <Paper
        ref={chartRef}
        elevation={0}
        variant="outlined"
        sx={{
          p: 4,
          bgcolor: '#ffffff',
          maxWidth: '1100px',
          mx: 'auto',
          borderRadius: 1.5,
        }}
      >
        <Box textAlign="center" sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: theme.palette.primary.dark, textTransform: 'uppercase' }}
          >
            Sector Facility Analytics - {stateSelected?.stateName} State
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Visual distribution of facilities categorized by Sectors and LGAs
          </Typography>
        </Box>

        {/* Chart 1: Facilities per Sector */}
        <Paper variant="outlined" sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
            FACILITIES DISTRIBUTION PER INDUSTRIAL SECTOR
          </Typography>
          <Box sx={{ width: '100%', height: 350, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sectorChartData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                <XAxis
                  dataKey="sectorName"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar
                  dataKey="count"
                  name="Number of Facilities"
                  fill={theme.palette.primary.main}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Chart 2: LGA Share */}
        <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
            LOCAL GOVERNMENT SHARE
          </Typography>
          <Box sx={{ width: '100%', height: 320, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={lgaChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {lgaChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Paper>
    );
  };

  return (
    <Box sx={{ height: '90vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Grid2 container sx={{ height: '100%' }} spacing={0}>
        {/* Main Display Area */}
        <Grid2
          size={{ lg: 9, md: 8, sm: 12, xs: 12 }}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: '#f8f9fa',
          }}
        >
          {/* Top Header Action Bar with View Tabs */}
          <Box
            sx={{
              px: 3,
              py: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 48,
            }}
          >
            {/* View Mode Switcher */}
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              size="small"
              sx={{ minHeight: 38 }}
            >
              <Tab
                icon={<TableChartOutlined sx={{ fontSize: '1rem' }} />}
                iconPosition="start"
                label="Tabular"
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 38, fontSize: '0.8rem' }}
              />
              <Tab
                icon={<BarChartOutlined sx={{ fontSize: '1rem' }} />}
                iconPosition="start"
                label="Graphical"
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 38, fontSize: '0.8rem' }}
              />
            </Tabs>

            {facilityList.length > 0 && (
              <Stack direction="row" spacing={1}>
                {activeTab === 0 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<FileDownloadOutlined />}
                    onClick={exportToExcel}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                  >
                    Export Excel
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<PictureAsPdfOutlined />}
                  onClick={exportToPDF}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                >
                  Export PDF
                </Button>
              </Stack>
            )}
          </Box>

          {/* Report Main Scrollable Workspace */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
            {loading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Generating Sector Report...
                </Typography>
              </Stack>
            ) : facilityList.length === 0 ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: '100%', color: 'text.secondary' }}
              >
                <Typography variant="h6">No Sector Report Generated</Typography>
                <Typography variant="body2">
                  Select a State in the right controls panel and click "Preview".
                </Typography>
              </Stack>
            ) : activeTab === 1 ? (
              renderChartsView()
            ) : (
              /* Printable Report Container */
              <Paper
                id="report-pdf-container"
                ref={reportRef}
                elevation={0}
                variant="outlined"
                sx={{
                  p: 4,
                  bgcolor: '#ffffff',
                  maxWidth: '1100px',
                  mx: 'auto',
                  borderRadius: 1.5,
                }}
              >
                {/* Title Header */}
                <Box textAlign="center" sx={{ mb: 3 }}>
                  <Typography
                    className="report-title"
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      color: theme.palette.primary.dark,
                      textTransform: 'uppercase',
                      tracking: 1,
                    }}
                  >
                    List of Industries in {stateSelected?.stateName} State by Sectors
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Official Environmental & Sectoral Facility Inventory
                  </Typography>
                </Box>

                {/* Summary Cards */}
                <Grid2 container spacing={2} sx={{ mb: 4 }}>
                  <Grid2 size={{ xs: 4 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: 1,
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <BusinessOutlined color="primary" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          TOTAL FACILITIES
                        </Typography>
                        <Typography className="metric-value" variant="h6" fontWeight={700}>
                          {facilityList.length}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid2>

                  <Grid2 size={{ xs: 4 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.info.main, 0.05),
                        border: 1,
                        borderColor: alpha(theme.palette.info.main, 0.2),
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <CategoryOutlined color="info" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          SECTORS
                        </Typography>
                        <Typography className="metric-value" variant="h6" fontWeight={700}>
                          {totalSectors}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid2>

                  <Grid2 size={{ xs: 4 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        border: 1,
                        borderColor: alpha(theme.palette.success.main, 0.2),
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <LocationOnOutlined color="success" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          COVERED LGAs
                        </Typography>
                        <Typography className="metric-value" variant="h6" fontWeight={700}>
                          {totalLgas}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid2>
                </Grid2>

                {/* Main Data Table */}
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                          FACILITY NAME
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>SECTOR</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                          CATEGORY
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                          LOCAL GOVERNMENT
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(groupedSectors).map((sectorName) => {
                        const sectorFacilities = groupedSectors[sectorName];

                        return (
                          <>
                            {/* Group Header Row for Sector */}
                            <TableRow key={`header-${sectorName}`} sx={{ bgcolor: 'grey.100' }}>
                              <TableCell
                                colSpan={4}
                                align="center"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.8rem',
                                  py: 1,
                                  color: theme.palette.primary.dark,
                                  borderTop: '2px solid',
                                  borderBottom: '2px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                {sectorName} Sector
                              </TableCell>
                            </TableRow>

                            {/* Facilities under this sector */}
                            {sectorFacilities.map((facility, index) => (
                              <TableRow
                                key={facility.facilityId || `${sectorName}-${index}`}
                                hover
                                sx={{
                                  '&:nth-of-type(even)': { bgcolor: 'grey.50' },
                                }}
                              >
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>
                                  {facility.facilityName || '-'}
                                </TableCell>

                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>
                                  {facility.sectorName || '-'}
                                </TableCell>

                                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>
                                  {facility.facilityCategoryName ? (
                                    <Chip
                                      label={facility.facilityCategoryName}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.675rem',
                                        height: 20,
                                        fontWeight: 600,
                                        textTransform: 'capitalize',
                                      }}
                                    />
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>

                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    color: 'text.primary',
                                    py: 1,
                                  }}
                                >
                                  {facility.lgaName || '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        </Grid2>

        {/* Right Sidebar Controls */}
        <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }} sx={{ height: '100%', bgcolor: 'white' }}>
          <Stack sx={{ height: '90%' }}>
            {/* Control Header */}
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList color="primary" fontSize="small" />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Preview Controls
                </Typography>
              </Stack>
            </Box>

            {/* Selectors Form */}
            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              <Stack spacing={3}>
                <Autocomplete
                  fullWidth
                  options={stateList}
                  loading={stateLoading}
                  getOptionLabel={(option) => option.stateName || ''}
                  value={stateSelected.stateId ? stateSelected : null}
                  onChange={(_, value) => setStateSelected(value || initState)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      size="small"
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {stateLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Action Footer */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <VisibilityOutlined />
                }
                disabled={loading || !stateSelected?.stateId}
                onClick={handlePreviewReport}
                sx={{
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              >
                Preview
              </Button>
            </Box>
          </Stack>
        </Grid2>
      </Grid2>
    </Box>
  );
}
